import BaseModel from "./BaseModel";
import {
    action,
    computed,
    makeObservable,
    observable,
} from "mobx";

import {isValidPhoneNumber} from "libphonenumber-js";

import {post} from "../common/requester";
import Location_ from "./Location_";


const normalizePhone = (value) => {
    if (!value) {
        return "";
    }

    const digits =
        String(value).replace(/\D/g, "");

    return digits
        ? `+${digits}`
        : "";
};


const validatePhone = (value) => {
    const phone =
        normalizePhone(value);

    if (!phone) {
        return false;
    }

    try {
        return isValidPhoneNumber(phone);
    } catch {
        return false;
    }
};


export default class Company extends BaseModel {

    constructor(store) {
        super(store);

        makeObservable(this, {
            id: observable,

            name: observable,
            owner_phone: observable,
            about: observable,
            phone_number: observable,

            telegram: observable,
            whatsapp: observable,
            instagram: observable,
            website: observable,

            photo_upload: observable,
            logo: observable,

            country_id: observable,
            region_id: observable,
            district_id: observable,

            sports: observable,

            location: observable,
            locations: observable,

            was_validated: observable,

            is_valid_owner_phone: computed,
            is_valid_phone_number: computed,
            locations_valid: computed,
            is_valid: computed,

            validate: action,

            getForEdit: action,

            setLocations: action,
            addLocation: action,
            deleteLocation: action,

            saveCompany: action,
        });
    }


    id = 0;

    name = "";
    owner_phone = "";
    about = "";
    phone_number = "";

    telegram = "";
    whatsapp = "";
    instagram = "";
    website = "";

    photo_upload = null;

    logo =
        "https://placehold.co/200?text=No+Photo&font=roboto";


    /*
     * Legacy single-location field.
     * Keep it for API compatibility for now.
     * New UI uses locations[].
     */
    location = {
        lat: 0,
        lng: 0,
    };


    locations = [
        new Location_(),
    ];


    sports = [];

    country_id = "";
    region_id = "";
    district_id = "";

    was_validated = false;


    /* =====================================================
       PHONE VALIDATION
       ===================================================== */

    get is_valid_owner_phone() {
        return validatePhone(
            this.owner_phone
        );
    }


    get is_valid_phone_number() {
        return validatePhone(
            this.phone_number
        );
    }


    /* =====================================================
       LOCATION VALIDATION
       ===================================================== */

    get locations_valid() {
        if (
            !Array.isArray(this.locations) ||
            this.locations.length === 0
        ) {
            return false;
        }


        /*
         * Location_ will own its own validation.
         *
         * For backward compatibility an old Location_
         * without is_valid is not rejected here.
         */
        return this.locations.every(
            (location) =>
                location?.is_valid !== false
        );
    }


    /* =====================================================
       COMPANY VALIDATION
       ===================================================== */

    get is_valid() {
        return Boolean(
            this.name?.trim() &&
            this.about?.trim() &&

            this.is_valid_owner_phone &&
            this.is_valid_phone_number &&

            this.country_id &&
            this.region_id &&

            Array.isArray(this.sports) &&
            this.sports.length > 0 &&

            this.locations_valid
        );
    }


    validate() {
        this.was_validated = true;


        (this.locations || []).forEach(
            (location) => {
                location.validate?.();
            }
        );


        return this.is_valid;
    }


    /* =====================================================
       LOAD
       ===================================================== */

    async getForEdit(company_id) {
        const res =
            await post(
                "company",
                {
                    company_id,
                }
            );


        if (!res?.company) {
            return res;
        }


        this.fromJson(
            res.company
        );


        this.setLocations(
            res.company.locations
        );


        this.was_validated = false;


        return res;
    }


    /* =====================================================
       LOCATIONS
       ===================================================== */

    setLocations(locations) {
        const items =
            Array.isArray(locations)
                ? locations
                : [];


        this.locations =
            items.map((item) => {
                const location =
                    new Location_();


                location.id =
                    item.id;

                location.name =
                    item.name || "";

                location.address =
                    item.address || "";

                location.location =
                    item.location || null;

                location.is_main =
                    Boolean(item.is_main);


                return location;
            });


        /*
         * Company must always have at least
         * one editable location.
         */
        if (
            this.locations.length === 0
        ) {
            this.addLocation();
            return;
        }


        /*
         * Normalize main location.
         *
         * If backend returned no main location,
         * make the first one main.
         *
         * If backend somehow returned several,
         * keep only the first.
         */
        let mainFound = false;


        this.locations.forEach(
            (location) => {
                if (
                    location.is_main &&
                    !mainFound
                ) {
                    mainFound = true;
                    return;
                }

                if (location.is_main) {
                    location.is_main = false;
                }
            }
        );


        if (!mainFound) {
            this.locations[0].is_main = true;
        }
    }


    addLocation() {
        const location =
            new Location_();


        const ids =
            (this.locations || [])
                .map((item) =>
                    Number(item.id)
                )
                .filter(
                    Number.isFinite
                );


        const minId =
            ids.length > 0
                ? Math.min(...ids)
                : 0;


        /*
         * Unsaved locations get negative ids:
         * -1, -2, -3...
         */
        location.id =
            Math.min(
                0,
                minId
            ) - 1;


        location.name = "";
        location.address = "";

        /*
         * Do not insert 0/0 or user position.
         * Map center and selected point are
         * different concepts.
         */
        location.location = null;


        location.is_main =
            (this.locations?.length || 0) === 0;


        this.locations.push(
            location
        );
    }


    deleteLocation(location_id) {
        if (
            !Array.isArray(this.locations) ||
            this.locations.length <= 1
        ) {
            return;
        }


        const deletedLocation =
            this.locations.find(
                (location) =>
                    location.id ===
                    location_id
            );


        this.locations =
            this.locations.filter(
                (location) =>
                    location.id !==
                    location_id
            );


        /*
         * If main location was removed,
         * promote the first remaining location.
         */
        if (
            deletedLocation?.is_main &&
            this.locations.length > 0
        ) {
            this.locations.forEach(
                (location, index) => {
                    location.is_main =
                        index === 0;
                }
            );
        }
    }


    /* =====================================================
       SAVE
       ===================================================== */

    async saveCompany() {
        if (!this.validate()) {
            throw new Error(
                "please_check_data"
            );
        }


        const res =
            await post(
                "company_save",
                this.toJson()
            );


        if (
            res &&
            res.result === 0 &&
            res.company
        ) {
            this.fromJson(
                res.company
            );


            this.setLocations(
                res.company.locations
            );


            this.setProperty(
                "photo_upload",
                null
            );


            this.was_validated = false;
        }


        return res;
    }
}